import { z } from 'zod';
import { pendingActionSchema, watchScriptSchema } from './assistantActions.js';
import { serviceKeySchema, serviceRequestSchema } from './services.js';
import { viewHintSchema } from './views.js';
/**
 * Conversa com o Secretário. O que o usuário vê enquanto ele trabalha são
 * atividades públicas ("Conferindo sua agenda"), nunca raciocínio interno.
 */
/**
 * Ação que o Secretário deixa pronta e o app executa só depois de o dono
 * confirmar. Mensagens entre pessoas são cifradas no aparelho: o servidor
 * prepara o rascunho, quem envia é o app.
 */
export const assistantActionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('send_message'),
        contactId: z.uuid(),
        contactName: z.string(),
        /** @usuário de quem já está no Secretário; null quando ainda não está. */
        username: z.string().nullable(),
        text: z.string().min(1).max(2000),
        /** O contato tem telefone: o app pode abrir a mensagem pronta no WhatsApp (apps antigos ignoram). */
        whatsapp: z.boolean().optional(),
    }),
]);
/**
 * Cartão visual que acompanha o resultado de uma ferramenta (agenda, tempo,
 * resumo do dia). Montado por código a partir dos dados, nunca pelo modelo.
 */
export const assistantCardSchema = z.object({
    /** `sources`: as fontes de uma pesquisa na internet (título e link de cada uma). */
    kind: z.enum(['agenda', 'weather', 'day', 'sources']),
    title: z.string(),
    /** Número em destaque (temperatura, quantidade). */
    value: z.string().nullable().default(null),
    rows: z
        .array(z.object({
        when: z.string().nullable().default(null),
        text: z.string(),
        tone: z.enum(['neutral', 'good', 'warn']).default('neutral'),
        /** Link da fonte (só nas fontes de pesquisa). */
        url: z.url().nullable().optional(),
    }))
        .max(6)
        .default([]),
    href: z.string().nullable().default(null),
    /** Habilidade da Equipe que ilustra o cartão (ex.: "contas"); sem ela, a arte do tipo. */
    art: z.string().max(40).optional(),
    /** Texto do link do cartão (ex.: "Abrir a lista"); sem ele, o do tipo. */
    more: z.string().max(40).optional(),
    /** O que este cartão acabou de criar, para o "Desfazer" (apps antigos ignoram). */
    undo: z.object({ kind: z.enum(['reminder', 'appointment']), id: z.uuid() }).optional(),
});
/**
 * Pedido de documento pronto que o Secretário deixou montado na conversa. O app
 * mostra o resumo e só cria o documento (POST /v1/deliveries) quando o dono
 * toca em Preparar; nada é gasto antes disso. Campo novo e opcional da
 * atividade: os apps antigos descartam sem quebrar.
 */
export const assistantConfirmSchema = z.object({
    kind: z.literal('service'),
    service: serviceKeySchema,
    title: z.string(),
    /** Resumo do pedido, para a pessoa conferir antes de preparar. */
    rows: z.array(z.object({ label: z.string(), value: z.string() })).max(8),
    /** O pedido já validado, pronto para POST /v1/deliveries. */
    request: serviceRequestSchema,
    /** Documentos que ainda cabem no mês (null = sem limite conhecido). */
    remaining: z.number().int().nullable(),
});
export const assistantActivitySchema = z.object({
    id: z.string(),
    /** Nome estável da ferramenta, para o app escolher o ícone. */
    tool: z.string(),
    label: z.string(),
    state: z.enum(['running', 'done', 'failed']),
    action: assistantActionSchema.optional(),
    card: assistantCardSchema.optional(),
    /** Painel ou aviso que este resultado pede ao palco (apps antigos ignoram). */
    view: viewHintSchema.optional(),
    /** Pedido de documento à espera do toque do dono (apps antigos ignoram). */
    confirm: assistantConfirmSchema.optional(),
    /** Ação à espera do sim (F0): o app mostra Sim e Mudar; o toque decide no servidor. */
    pending: pendingActionSchema.optional(),
    /** O roteiro do modo Assistir (F1): a tela repete o que o servidor fez. */
    watch: watchScriptSchema.optional(),
    /** A tela que o app abre agora (F3: "abre a agenda" pelo texto); só telas da lista fechada. */
    open: z.string().startsWith('/').max(120).optional(),
    /**
     * Opções para responder com um toque (ex.: as unidades de uma rede no Perto). O toque manda
     * `send` como fala da pessoa, com o `lugar` exato quando houver. Apps antigos ignoram.
     */
    choices: z
        .array(z.object({
        label: z.string().max(80),
        sub: z.string().max(120).nullable().default(null),
        send: z.string().max(200),
        lugar: z.string().max(64).nullable().default(null),
    }))
        .max(6)
        .optional(),
});
/**
 * Anexos da conversa com o Secretário (foto, PDF e arquivos de texto: Word, Excel,
 * PowerPoint, TXT, CSV). O arquivo sobe antes (POST /v1/assistant/attachments) e a
 * mensagem leva só os ids. Ficam 30 dias; guardar para sempre é em Guardados.
 */
export const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
export const ATTACHMENTS_PER_MESSAGE = 4;
/** Limite justo por dia (fotos e arquivos), invisível até ser atingido. */
export const ATTACHMENTS_DAILY = { free: 3, basic: 30, pro: 80 };
export const ATTACHMENT_IMAGE_MIMES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
];
export const ATTACHMENT_TEXT_MIMES = {
    'text/plain': 'txt',
    'text/csv': 'csv',
    'text/markdown': 'md',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
};
/** O que o seletor de arquivos do app oferece (atributo `accept`). */
export const ATTACHMENT_ACCEPT = [
    ...ATTACHMENT_IMAGE_MIMES,
    'application/pdf',
    ...Object.keys(ATTACHMENT_TEXT_MIMES),
    '.txt',
    '.csv',
    '.md',
    '.docx',
    '.xlsx',
    '.pptx',
].join(',');
export const assistantAttachmentSchema = z.object({
    id: z.uuid(),
    kind: z.enum(['image', 'pdf', 'text']),
    name: z.string().max(200),
    mime: z.string().max(120),
    size: z.number().int().nonnegative(),
});
export const assistantTurnSchema = z.object({
    id: z.uuid(),
    role: z.enum(['owner', 'assistant']),
    text: z.string(),
    activities: z.array(assistantActivitySchema),
    /** Anexos que o dono mandou nesta fala (apps antigos ignoram). */
    attachments: z.array(assistantAttachmentSchema).max(ATTACHMENTS_PER_MESSAGE).optional(),
    createdAt: z.iso.datetime(),
});
export const assistantHistorySchema = z.object({
    turns: z.array(assistantTurnSchema),
});
export const sendAssistantMessageSchema = z
    .object({
    /** Pode vir vazio quando há anexo ("o que você me diz disso?" fica implícito). */
    text: z.string().trim().max(2000),
    /**
     * O que este app sabe mostrar. Ferramentas que dependem de uma tela nova só
     * entram na conversa quando o app declara a capacidade: `docs` (pedido de
     * documento confirmado na tela) e `goals` (a página Objetivos). Apps antigos
     * não mandam nada.
     */
    caps: z.array(z.string().max(24)).max(16).optional(),
    /** Anexos já enviados (ids de POST /v1/assistant/attachments), até 4. */
    attachments: z.array(z.uuid()).max(ATTACHMENTS_PER_MESSAGE).optional(),
    /** De onde veio o pedido (F3): no balão da clínica, "abre a agenda" é a agenda da clínica. */
    place: z.enum(['pessoal', 'clinica']).optional(),
    /** O lugar do Perto que a pessoa abriu e de onde pediu ("Pedir ao Secretário"). */
    lugar: z.string().trim().min(1).max(64).optional(),
})
    .refine((body) => body.text.length > 0 || (body.attachments?.length ?? 0) > 0, {
    message: 'Escreva algo ou mande um anexo.',
    path: ['text'],
});
/** Eventos do fluxo (`text/event-stream`) de uma resposta do Secretário. */
export const assistantStreamEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('owner'), turn: assistantTurnSchema }),
    z.object({ type: z.literal('activity'), activity: assistantActivitySchema }),
    z.object({ type: z.literal('delta'), text: z.string() }),
    z.object({ type: z.literal('done'), turn: assistantTurnSchema }),
    z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
]);
/** Capacidades que um app pode declarar em `caps`. */
export const ASSISTANT_CAP_DOCS = 'docs';
/** O app tem a página Objetivos: libera as ferramentas de objetivos na conversa. */
export const ASSISTANT_CAP_GOALS = 'goals';
/** O app desenha o visual vivo (barba do personagem a traço): libera a ferramenta do visual. */
export const ASSISTANT_CAP_LOOK = 'look';
/** O app tem a página Receitas: libera as ferramentas de receita na conversa. */
export const ASSISTANT_CAP_RECIPES = 'recipes';
/** O app tem os Documentos com busca, fixados, lixeira e Guardados: libera a procura por voz. */
export const ASSISTANT_CAP_HD = 'hd';
/**
 * O app desenha o cartão de saída: rota (Maps, Waze), corrida (Uber) e ligação, que abrem o
 * app escolhido já preenchido depois do toque do dono. O cartão usa o tipo `sources` (links
 * em linhas, que todas as versões entendem) com a arte `passagens`; apps antigos mostram os
 * mesmos links como lista.
 */
export const ASSISTANT_CAP_LINKS = 'links';
/** Arte que marca o cartão de saída dentro do tipo `sources`. */
export const DEPARTURE_CARD_ART = 'passagens';
